// =============================================================================
// features/sales/campaigns/CampaignsFilters.tsx — Filtros por status,
// proprietário, produto e período (doc 26 §6.5). Todos os quatro filtros que
// a API realmente aceita (packages/shared-schemas/src/sales-campaigns.ts
// CampaignListQuerySchema) — nenhum filtro fantasma.
//
// "Proprietário" vira um toggle "Minhas campanhas" em vez de um seletor de
// pessoa: resolver nome de usuário exigiria `/api/admin/users`
// (`admin_users:read`), permissão que um gestor comercial comum não tem —
// listar usuários errado silenciosamente já causou incidente neste
// codebase (agent_id vs users.id). O toggle usa o próprio `user.id` da
// sessão (sempre correto) e só aparece para quem tem `sales_campaigns:manage`
// (sem essa permissão a API já restringe ao próprio ator, ver rota).
//
// "Produto" degrada graciosamente: se `/api/credit-products` não estiver
// acessível ao papel do ator (403/erro), o filtro simplesmente não aparece —
// nunca quebra a tela inteira por um dropdown secundário.
// =============================================================================

import type { CampaignStatus } from '@elemento/shared-schemas';
import * as React from 'react';

import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useProducts } from '../../../hooks/admin/useProducts';
import { cn } from '../../../lib/cn';

const STATUS_OPTIONS: { value: CampaignStatus; label: string }[] = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'ready', label: 'Pronta' },
  { value: 'active', label: 'Ativa' },
  { value: 'paused', label: 'Pausada' },
  { value: 'completed', label: 'Concluída' },
  { value: 'cancelled', label: 'Cancelada' },
];

export interface CampaignsFiltersProps {
  status: CampaignStatus[];
  onStatusChange: (status: CampaignStatus[]) => void;
  productId: string | null;
  onProductIdChange: (id: string | null) => void;
  canFilterByOwner: boolean;
  mine: boolean;
  onMineChange: (mine: boolean) => void;
  createdFrom: string;
  createdTo: string;
  onCreatedRangeChange: (from: string, to: string) => void;
}

export function CampaignsFilters({
  status,
  onStatusChange,
  productId,
  onProductIdChange,
  canFilterByOwner,
  mine,
  onMineChange,
  createdFrom,
  createdTo,
  onCreatedRangeChange,
}: CampaignsFiltersProps): React.JSX.Element {
  const { data: productsData, isError: productsError } = useProducts({ limit: 100 });
  const products = productsData?.data ?? [];

  // Select único aceita 1 status por vez — o backend suporta múltiplos, mas
  // a UI de lista fica mais previsível com um recorte por vez (limpar =
  // "todos"). "Ativas" segue selecionável direto pelo card de indicador.
  const singleStatus = status[0] ?? '';

  return (
    <div
      className="flex flex-wrap items-end gap-3 rounded-md border border-border p-4"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
      role="group"
      aria-label="Filtros de campanhas"
    >
      <Select
        id="campaigns-filter-status"
        label="Status"
        placeholder="Todos os status"
        wrapperClassName="min-w-[160px]"
        options={STATUS_OPTIONS}
        value={singleStatus}
        onChange={(e) => onStatusChange(e.target.value ? [e.target.value as CampaignStatus] : [])}
      />

      {!productsError && (
        <Select
          id="campaigns-filter-product"
          label="Produto"
          placeholder="Todos os produtos"
          wrapperClassName="min-w-[180px]"
          options={products.map((p) => ({ value: p.id, label: p.name }))}
          value={productId ?? ''}
          onChange={(e) => onProductIdChange(e.target.value || null)}
        />
      )}

      <Input
        id="campaigns-filter-created-from"
        type="date"
        label="Criada de"
        value={createdFrom}
        onChange={(e) => onCreatedRangeChange(e.target.value, createdTo)}
      />

      <Input
        id="campaigns-filter-created-to"
        type="date"
        label="Criada até"
        value={createdTo}
        onChange={(e) => onCreatedRangeChange(createdFrom, e.target.value)}
      />

      {canFilterByOwner && (
        <label
          className={cn(
            'inline-flex items-center gap-2 h-[44px] px-3 rounded-sm border border-border-strong',
            'font-sans text-sm font-medium text-ink cursor-pointer select-none',
            'hover:bg-surface-hover transition-colors duration-fast',
            'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-azul/40',
          )}
        >
          <input
            type="checkbox"
            checked={mine}
            onChange={(e) => onMineChange(e.target.checked)}
            className="w-4 h-4 accent-azul"
          />
          Minhas campanhas
        </label>
      )}
    </div>
  );
}
